import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { alpha, Box, GlobalStyles, keyframes, Typography } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';

import {
  getPdfJsDocumentOptions,
  resolvePdfJsWorkerSrc
} from '@host/components/NewUI/utils/pdfjs-config';

import { onboardColors } from '../../theme/onboardTokens';

export const DEFAULT_PAGE_WIDTH = 794; // A4 @ 96dpi

const clauseHighlight = keyframes({
  '0%': { opacity: 0 },
  '12%, 68%': { opacity: 1 },
  '100%': { opacity: 0 }
});

const fadeIn = keyframes({ '0%': { opacity: 0 }, '100%': { opacity: 1 } });

const shimmer = keyframes({
  '0%': { backgroundPosition: '200% 0' },
  '100%': { backgroundPosition: '-200% 0' }
});

const ADDING_SKELETON_HEIGHT_PX = 22;
// Inline words sit among other lines of text, so their highlight hugs them more tightly.
const WORDS_PADDING_X_PX = 4;
const WORDS_PADDING_Y_PX = 2;
// The skeleton shown where inline words are about to appear.
const ADDING_WORDS_WIDTH_PX = 48;
// The most filled-in placeholder text expected between two parts of the words.
const MAX_PLACEHOLDER_LENGTH = 200;

// 'added' briefly highlights a section that has just appeared; 'removed' fades a section that
// is about to go; 'adding' shows a loading skeleton where a section will appear, just after
// one of `highlightAnchors`. The last two stay until the next document replaces this one.
export type TPdfHighlightTone = 'added' | 'removed' | 'adding';

// 'section' runs on to the next heading or paragraph gap; 'row' covers one table row's line;
// 'inline' is words within a paragraph, highlighted line by line.
export type TPdfHighlightScope = 'section' | 'row' | 'inline';

// The exact text a highlight covers, so it stops where that text ends rather than at the next
// wide gap between lines. It is split where a placeholder's filled-in text sits, so an empty
// first or last part means it starts or ends with one. `before` and `after` are the text
// either side of it.
export interface IPdfHighlightWords {
  parts: string[];
  before?: string;
  after?: string;
}

export interface IPdfHighlightAnchor {
  text: string;
  scope: TPdfHighlightScope;
  // The anchor's whole text, so a skeleton goes just below its last line.
  words?: IPdfHighlightWords;
}

interface IRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface IBlockBounds extends IRect {
  // The span the block starts at, so a later, better match can be told apart.
  start: HTMLElement;
  page: HTMLElement;
}

interface ITextPoint {
  node: Text;
  offset: number;
}

// A text layer's characters without spaces, each with where it sits, so words are found
// however the PDF split them into spans.
interface ITextIndex {
  text: string;
  points: ITextPoint[];
}

const normalizeText = (text: string): string =>
  text
    .normalize('NFKC')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .toLowerCase()
    .replace(/\s+/g, '');

const indexText = (textLayer: HTMLElement): ITextIndex => {
  const index: ITextIndex = { text: '', points: [] };
  const walker = document.createTreeWalker(textLayer, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const textNode = node as Text;
    for (let offset = 0; offset < textNode.data.length; offset += 1) {
      // A ligature can stand for more than one letter, so each letter keeps its place.
      Array.from(normalizeText(textNode.data[offset])).forEach((char) => {
        index.text += char;
        index.points.push({ node: textNode, offset });
      });
    }
  }
  return index;
};

const union = (rects: IRect[]): IRect => ({
  left: Math.min(...rects.map((rect) => rect.left)),
  top: Math.min(...rects.map((rect) => rect.top)),
  right: Math.max(...rects.map((rect) => rect.right)),
  bottom: Math.max(...rects.map((rect) => rect.bottom))
});

// The on-screen boxes of the characters from `first` to `last`, one per line of text.
const lineRects = (points: ITextPoint[], first: number, last: number): IRect[] => {
  const nodes = new Map<Text, { start: number; end: number }>();
  points.slice(first, last + 1).forEach(({ node, offset }) => {
    const found = nodes.get(node);
    nodes.set(node, { start: found?.start ?? offset, end: offset + 1 });
  });
  const rects = Array.from(nodes).flatMap(([node, { start, end }]) => {
    const range = document.createRange();
    if (typeof range.getClientRects !== 'function') return [];
    range.setStart(node, start);
    range.setEnd(node, end);
    return Array.from(range.getClientRects()).filter((rect) => rect.width > 0);
  });

  const lines: IRect[] = [];
  rects
    .sort((a, b) => a.top - b.top || a.left - b.left)
    .forEach((rect) => {
      const line = lines.at(-1);
      const middle = (rect.top + rect.bottom) / 2;
      if (line && middle > line.top && middle < line.bottom) {
        lines[lines.length - 1] = union([line, rect]);
      } else {
        lines.push({ left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom });
      }
    });
  return lines;
};

// The last character on the same line of text as the one at `at`.
const lineEnd = (points: ITextPoint[], at: number): number => {
  const topOf = (index: number) =>
    points[index].node.parentElement?.getBoundingClientRect().top ?? 0;
  const top = topOf(at);
  let end = at;
  while (end + 1 < points.length && Math.abs(topOf(end + 1) - top) < 2) end += 1;
  return end;
};

// Where the words sit in the text, searching from `from`: the index of their first and last
// characters. Each part follows the last within a placeholder's length, words that start
// with a placeholder start at `from`, and words that end with one end where `after` starts,
// or at the end of their line when nothing follows them.
const matchWords = (
  { text, points }: ITextIndex,
  from: number,
  words: IPdfHighlightWords
): [number, number] | null => {
  let first = -1;
  let cursor = from;
  const parts = words.parts.map(normalizeText);
  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index];
    if (part) {
      const at = text.indexOf(part, cursor);
      if (at < 0 || (index > 0 && at - cursor > MAX_PLACEHOLDER_LENGTH)) return null;
      if (first < 0) first = index === 0 ? at : cursor;
      cursor = at + part.length;
    }
  }
  if (first < 0) first = from;
  let last = cursor - 1;
  if (parts.length > 1 && parts.at(-1) === '') {
    const after = normalizeText(words.after ?? '');
    if (!after) return [first, lineEnd(points, Math.max(first, last))];
    const afterAt = text.indexOf(after, cursor);
    if (afterAt < 0 || afterAt - cursor > MAX_PLACEHOLDER_LENGTH) return null;
    last = afterAt - 1;
  }
  return last < first ? null : [first, last];
};

// The text of the block's text layer, and where in it the block starts. Words found in it
// can run past the block, since a block stops at the first wide gap between lines.
const blockText = (block: IBlockBounds): (ITextIndex & { start: number }) | null => {
  const textLayer = block.start.closest<HTMLElement>('.textLayer');
  if (!textLayer) return null;
  const index = indexText(textLayer);
  const start = index.points.findIndex(({ node }) => block.start.contains(node));
  return start < 0 ? null : { ...index, start };
};

// The lines the words cover, from the start of the block (or just after `before` in it).
// Null when the words cannot be found, so the block's own bounds are used instead.
const wordLines = (block: IBlockBounds, words: IPdfHighlightWords): IRect[] | null => {
  const found = blockText(block);
  if (!found) return null;
  const before = normalizeText(words.before ?? '');
  const beforeAt = before ? found.text.indexOf(before, found.start) : -1;
  const match = matchWords(found, beforeAt >= 0 ? beforeAt + before.length : found.start, words);
  if (!match) return null;
  const lines = lineRects(found.points, match[0], match[1]);
  return lines.length ? lines : null;
};

// The words wherever they are in the pages, for when their opening line is not one span.
const findWordsBlock = (
  container: HTMLElement | null,
  words: IPdfHighlightWords
): (IBlockBounds & { lines: IRect[] }) | null => {
  const textLayers = Array.from(container?.querySelectorAll<HTMLElement>('.textLayer') ?? []);
  let found: (IBlockBounds & { lines: IRect[] }) | null = null;
  textLayers.some((textLayer) => {
    const textIndex = indexText(textLayer);
    const match = matchWords(textIndex, 0, words);
    const page = textLayer.closest<HTMLElement>('.react-pdf__Page');
    const start = match ? textIndex.points[match[0]].node.parentElement : null;
    const lines = match ? lineRects(textIndex.points, match[0], match[1]) : [];
    if (page && start && lines.length) found = { start, page, lines, ...union(lines) };
    return found !== null;
  });
  return found;
};

// A short line where inline words are about to appear: just after the text before them, or
// at the start of the block when nothing comes before them.
const insertionLine = (block: IBlockBounds, words: IPdfHighlightWords): IRect[] | null => {
  const found = blockText(block);
  if (!found) return null;
  const before = normalizeText(words.before ?? '');
  const beforeAt = before ? found.text.indexOf(before, found.start) : -1;
  const at = beforeAt >= 0 ? beforeAt + before.length - 1 : found.start;
  const [rect] = lineRects(found.points, at, at);
  if (!rect) return null;
  const left = beforeAt >= 0 ? rect.right + 2 : rect.left;
  return [{ left, top: rect.top, right: left + ADDING_WORDS_WIDTH_PX, bottom: rect.bottom }];
};

// Places the highlight over inline words, one box per line they run over.
const positionWordsOverlay = (overlay: HTMLElement, page: HTMLElement, lines: IRect[]): void => {
  const pageRect = page.getBoundingClientRect();
  const padded = lines.map((line) => ({
    left: line.left - WORDS_PADDING_X_PX,
    top: line.top - WORDS_PADDING_Y_PX,
    right: line.right + WORDS_PADDING_X_PX,
    bottom: line.bottom + WORDS_PADDING_Y_PX
  }));
  const box = union(padded);
  Object.assign(overlay.style, {
    left: `${box.left - pageRect.left}px`,
    width: `${box.right - box.left}px`,
    top: `${box.top - pageRect.top}px`,
    height: `${box.bottom - box.top}px`
  });
  overlay.replaceChildren(
    ...padded.map((line) => {
      const lineBox = document.createElement('span');
      Object.assign(lineBox.style, {
        left: `${line.left - box.left}px`,
        width: `${line.right - line.left}px`,
        top: `${line.top - box.top}px`,
        height: `${line.bottom - line.top}px`
      });
      return lineBox;
    })
  );
};

// Places the highlight over the block, or just below it for a section about to be added.
const positionOverlay = (overlay: HTMLElement, block: IBlockBounds, adding: boolean): void => {
  const pageRect = block.page.getBoundingClientRect();
  Object.assign(overlay.style, {
    left: `${block.left - pageRect.left - 8}px`,
    width: `${block.right - block.left + 16}px`,
    top: adding ? `${block.bottom - pageRect.top + 4}px` : `${block.top - pageRect.top - 8}px`,
    height: adding ? `${ADDING_SKELETON_HEIGHT_PX}px` : `${block.bottom - block.top + 16}px`
  });
};

// The element that draws a highlight's box: the overlay itself, or each line inside it.
const highlightBoxSelector = (tone: string): string =>
  `& [data-pdf-highlight="${tone}"]:not([data-pdf-highlight-words]), & [data-pdf-highlight="${tone}"] > span`;

/**
 * Stacked-pages PDF viewer shared by the contract template preview, the
 * template detail modal, and the candidate portal contract view.
 * `data` is a Blob (preview endpoints) or a URL string (portal asset routes).
 * `placeholder`, when given, stays in place of the pages until the first page
 * has painted (or the PDF fails to load), so a half-rendered document never shows.
 */
const PdfViewer = ({
  data,
  highlightAnchors,
  highlightDurationMs = 2500,
  highlightScope = 'section',
  highlightText,
  highlightTone = 'added',
  highlightWords,
  onHighlightComplete,
  onLoadError,
  onPageRendered,
  onReady,
  placeholder,
  width = DEFAULT_PAGE_WIDTH
}: {
  data: Blob | string;
  highlightAnchors?: IPdfHighlightAnchor[];
  highlightDurationMs?: number;
  highlightScope?: TPdfHighlightScope;
  highlightText?: string;
  highlightTone?: TPdfHighlightTone;
  highlightWords?: IPdfHighlightWords;
  onHighlightComplete?: () => void;
  onLoadError?: () => void;
  // Called as each page finishes drawing, before every page is ready.
  onPageRendered?: (pageNumber: number) => void;
  onReady?: () => void;
  placeholder?: React.ReactNode;
  width?: number;
}) => {
  const [numPages, setNumPages] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [paintedData, setPaintedData] = useState<Blob | string | null>(null);
  const documentOptions = useMemo(() => getPdfJsDocumentOptions(), []);
  const showPlaceholder = placeholder != null && paintedData !== data && loadError == null;
  const containerRef = useRef<HTMLDivElement>(null);
  const highlightOverlayRef = useRef<HTMLDivElement | null>(null);
  const highlightStartRef = useRef<HTMLElement | null>(null);
  // The latest highlightClause, for the re-measure after layout.
  const highlightClauseRef = useRef<() => void>(() => {});
  const remeasureFrameRef = useRef<number | null>(null);
  // Read through refs so a parent re-render (a new callback or a new anchors array with the
  // same content) does not rebuild the highlight and scroll back to it.
  const onHighlightCompleteRef = useRef(onHighlightComplete);
  const highlightAnchorsRef = useRef(highlightAnchors);
  const highlightWordsRef = useRef(highlightWords);
  useLayoutEffect(() => {
    onHighlightCompleteRef.current = onHighlightComplete;
    highlightAnchorsRef.current = highlightAnchors;
    highlightWordsRef.current = highlightWords;
  });
  const highlightTimerRef = useRef<number | null>(null);
  const expectedPagesRef = useRef(0);
  const renderedPagesRef = useRef(new Set<number>());
  const readyDataRef = useRef<Blob | string | null>(null);

  const clearHighlight = useCallback(() => {
    highlightOverlayRef.current?.remove();
    highlightOverlayRef.current = null;
    highlightStartRef.current = null;
  }, []);

  // The bounds of the block of text that starts with `text`: a paragraph or section runs on
  // to the next heading or paragraph gap, a table row covers its first line.
  const findBlock = useCallback((text: string, scope: TPdfHighlightScope): IBlockBounds | null => {
    // Prefer a span that is exactly the text, then one that starts with it, then any that
    // contains it, so "Induction" finds the table cell rather than a sentence mentioning it.
    const allSpans = Array.from(
      containerRef.current?.querySelectorAll<HTMLElement>('.textLayer span') ?? []
    );
    const spanText = (span: HTMLElement) => (span.textContent ?? '').replace(/\s+/g, ' ').trim();
    const matchingSpan =
      allSpans.find((span) => spanText(span) === text) ??
      allSpans.find((span) => spanText(span).startsWith(text)) ??
      allSpans.find((span) => spanText(span).includes(text));
    const page = matchingSpan?.closest<HTMLElement>('.react-pdf__Page');
    const textLayer = matchingSpan?.closest<HTMLElement>('.textLayer');
    if (!matchingSpan || !page || !textLayer) return null;

    // Blank spans (the gaps between words and table cells) sit off the text line, so they
    // are left out when measuring where a block ends.
    const spans = Array.from(textLayer.querySelectorAll<HTMLElement>('span')).filter((span) =>
      span.textContent?.trim()
    );
    const startIndex = spans.indexOf(matchingSpan);
    const blockSpans: HTMLElement[] = [];
    let previousRect: DOMRect | null = null;
    const firstLine = matchingSpan.getBoundingClientRect();
    for (let index = startIndex; index < spans.length; index += 1) {
      const span = spans[index];
      const rect = span.getBoundingClientRect();
      const startsNextClause = index > startIndex && /^\d+\.\s/.test(span.textContent ?? '');
      const startsNewBlock = previousRect && rect.top - previousRect.bottom > 20;
      // A table row's cells share its first line; the next row starts below it.
      const startsNextRow = scope === 'row' && rect.top >= firstLine.bottom;
      if (startsNextClause || startsNewBlock || startsNextRow) break;
      blockSpans.push(span);
      previousRect = rect;
    }
    if (!blockSpans.length) return null;

    return {
      start: matchingSpan,
      page,
      ...union(blockSpans.map((span) => span.getBoundingClientRect()))
    };
  }, []);

  const highlightClause = useCallback(() => {
    if (!highlightText) return;

    const adding = highlightTone === 'adding';
    const words = highlightWordsRef.current;
    const candidates: IPdfHighlightAnchor[] = adding
      ? (highlightAnchorsRef.current ?? [])
      : [{ text: highlightText, scope: highlightScope, words }];
    let found: IBlockBounds | null = null;
    let candidate = candidates[0];
    for (let index = 0; index < candidates.length && !found; index += 1) {
      candidate = candidates[index];
      found = findBlock(candidate.text, candidate.scope);
    }
    const foundWords =
      !found && !adding && words ? findWordsBlock(containerRef.current, words) : null;
    if (foundWords) found = foundWords;
    if (!found) return;

    // Inline words get a box per line. Anything else gets one box around its exact text,
    // or a skeleton just below the exact text of the anchor before it.
    let lines: IRect[] | null = null;
    let bounds = found;
    if (adding && !candidate.words && words && highlightScope === 'inline') {
      lines = insertionLine(found, words);
    } else if (candidate.words) {
      const content = foundWords?.lines ?? wordLines(found, candidate.words);
      if (content && candidate.scope === 'inline' && !adding) lines = content;
      else if (content) bounds = { ...found, ...union(content) };
    }
    // Pages draw their text in any order, so this runs again as each one finishes. The
    // highlight moves when a better match appears, and is re-measured each time because a
    // page's text can still be settling into place when it is first found.
    const sameMatch =
      highlightOverlayRef.current?.isConnected && highlightStartRef.current === found.start;
    let overlay = sameMatch ? highlightOverlayRef.current : null;
    if (!overlay) {
      clearHighlight();
      overlay = document.createElement('div');
      overlay.dataset.pdfHighlight = highlightTone === 'added' ? 'true' : highlightTone;
      if (lines) overlay.dataset.pdfHighlightWords = 'true';
      if (adding) overlay.setAttribute('aria-label', `Adding ${highlightText}`);
      found.page.append(overlay);
      highlightOverlayRef.current = overlay;
      highlightStartRef.current = found.start;
    }

    if (lines && overlay.dataset.pdfHighlightWords) {
      positionWordsOverlay(overlay, found.page, lines);
    } else {
      positionOverlay(overlay, bounds, adding);
    }
    if (sameMatch) return;

    // Measure once more after the browser has laid the text out.
    if (remeasureFrameRef.current) window.cancelAnimationFrame(remeasureFrameRef.current);
    remeasureFrameRef.current = window.requestAnimationFrame(() => highlightClauseRef.current());
    overlay.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
    if (highlightTone !== 'added') return;
    highlightTimerRef.current = window.setTimeout(() => {
      clearHighlight();
      onHighlightCompleteRef.current?.();
    }, highlightDurationMs);
  }, [
    clearHighlight,
    findBlock,
    highlightDurationMs,
    highlightScope,
    highlightText,
    highlightTone
  ]);

  useEffect(() => {
    highlightClauseRef.current = highlightClause;
  }, [highlightClause]);

  // A new highlight replaces the previous one. When the text layer is already drawn, it is
  // applied straight away rather than waiting for the next text-layer render.
  useEffect(() => {
    if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
    clearHighlight();
    if (highlightText) highlightClause();
  }, [clearHighlight, highlightClause, highlightText, highlightTone]);

  const markPageRendered = useCallback(
    (pageNumber: number) => {
      renderedPagesRef.current.add(pageNumber);
      onPageRendered?.(pageNumber);
      if (
        renderedPagesRef.current.size < expectedPagesRef.current ||
        readyDataRef.current === data
      ) {
        return;
      }

      readyDataRef.current = data;
      onReady?.();
    },
    [data, onPageRendered, onReady]
  );

  useEffect(() => {
    pdfjs.GlobalWorkerOptions.workerSrc = resolvePdfJsWorkerSrc(
      pdfjs.GlobalWorkerOptions.workerSrc
    );
  }, []);

  useEffect(
    () => () => {
      if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
      if (remeasureFrameRef.current) window.cancelAnimationFrame(remeasureFrameRef.current);
      clearHighlight();
    },
    [clearHighlight]
  );

  return (
    <>
      <GlobalStyles styles={{ ':root': { '--react-pdf-text-layer': 1 } }} />
      {showPlaceholder ? placeholder : null}
      <Box
        ref={containerRef}
        sx={{
          '& .textLayer': {
            forcedColorAdjust: 'none',
            inset: 0,
            lineHeight: 1,
            overflow: 'hidden',
            position: 'absolute',
            textAlign: 'initial',
            textSizeAdjust: 'none',
            transformOrigin: '0 0',
            zIndex: 2
          },
          '& .textLayer :is(span, br)': {
            color: 'transparent',
            cursor: 'text',
            margin: 0,
            position: 'absolute',
            transformOrigin: '0 0',
            whiteSpace: 'pre'
          },
          // A block highlight draws its own box; an inline one draws a box per line inside it.
          '& [data-pdf-highlight]': { pointerEvents: 'none', position: 'absolute', zIndex: 1 },
          '& [data-pdf-highlight] > span': { position: 'absolute' },
          '& [data-pdf-highlight="adding"]': {
            animation: `${fadeIn} 200ms ease-out forwards, ${shimmer} 1.4s linear infinite`
          },
          '& [data-pdf-highlight="adding"][data-pdf-highlight-words]': {
            animation: `${fadeIn} 200ms ease-out forwards`
          },
          [highlightBoxSelector('adding')]: {
            background: `linear-gradient(90deg, ${alpha(onboardColors.brand.teal, 0.18)} 25%, ${alpha(onboardColors.brand.teal, 0.42)} 50%, ${alpha(onboardColors.brand.teal, 0.18)} 75%)`,
            backgroundSize: '200% 100%',
            borderRadius: '4px'
          },
          '& [data-pdf-highlight="adding"] > span': {
            animation: `${shimmer} 1.4s linear infinite`
          },
          '& [data-pdf-highlight="removed"]': {
            animation: `${fadeIn} 200ms ease-out forwards`
          },
          [highlightBoxSelector('removed')]: {
            backgroundColor: alpha(onboardColors.surface.white, 0.75),
            border: `1px dashed ${onboardColors.border.divider}`,
            borderRadius: '4px'
          },
          '& [data-pdf-highlight="true"]': {
            animation: `${clauseHighlight} ${highlightDurationMs}ms ease-out forwards`,
            mixBlendMode: 'multiply'
          },
          [highlightBoxSelector('true')]: {
            backgroundColor: onboardColors.brandTint.teal50,
            borderRadius: '4px'
          },
          '& .react-pdf__Page': { position: 'relative' },
          display: showPlaceholder ? 'none' : 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
          backgroundColor: onboardColors.surface.fill,
          padding: '16px',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <Document
          file={data}
          options={documentOptions}
          loading={
            <Typography
              sx={{ color: onboardColors.neutral.textMutedAlt, fontSize: '13px', padding: '24px' }}
            >
              Loading document…
            </Typography>
          }
          onLoadError={(err) => {
            setLoadError(err.message);
            onLoadError?.();
          }}
          onLoadSuccess={(pdf) => {
            expectedPagesRef.current = pdf.numPages;
            renderedPagesRef.current.clear();
            readyDataRef.current = null;
            setNumPages(pdf.numPages);
            setLoadError(null);
            if (pdf.numPages === 0) onReady?.();
          }}
        >
          {Array.from({ length: numPages }, (_value, index) => (
            <Box
              key={`pdf-page-${index + 1}`}
              sx={{
                backgroundColor: onboardColors.surface.white,
                boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                marginBottom: index + 1 < numPages ? '24px' : 0
              }}
            >
              <Page
                onRenderError={index === 0 ? (err) => setLoadError(err.message) : undefined}
                onRenderTextLayerSuccess={highlightClause}
                pageNumber={index + 1}
                renderAnnotationLayer={false}
                renderTextLayer={Boolean(highlightText)}
                width={width}
                onRenderSuccess={() => {
                  if (index === 0) setPaintedData(data);
                  markPageRendered(index + 1);
                }}
              />
            </Box>
          ))}
        </Document>
        {loadError ? (
          <Typography
            sx={{ color: onboardColors.status.errorText, fontSize: '13px', padding: '24px' }}
          >
            {loadError}
          </Typography>
        ) : null}
      </Box>
    </>
  );
};

export default PdfViewer;
